import { ChangeDetectionStrategy, Component, inject, input, Input, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../../../../components/state/state.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IFormModel } from '../../../../../apis/form/model/form.model';
import { FormType, IFormBettings } from '@oogaday/shared';
import { NotificationService } from '../../../../../services/notification.service';
import { CdkPortal } from '@angular/cdk/portal';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { PlayoffFormFillerComponent } from '../common/components/playoff-form-filler/playoff-form-filler.component';
import { TournamentGeneralFormFillerComponent } from '../common/components/tournament-general-form-filler/tournament-general-form-filler.component';
import { GroupStageFormFillerComponent } from '../common/components/group-stage-form-filler/group-stage-form-filler.component';
import { MatchFormFillerComponent } from '../common/components/match-form-filler/match-form-filler.component';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { LoadingContainerComponent } from '../../../../../components/loading-container/loading-container.component';
import { NavBackComponent } from '../../../../../components/main/app-bar/nav-back/nav-back.component';
import { OverlaySpinnerComponent } from '../../../../../components/overlay-spinner/overlay-spinner.component';
import { DatePipe } from '@angular/common';
import { ChampionsLeagueStageComponent } from '../common/components/champions-league-stage/champions-league-stage.component';

@Component({
  selector: 'oo-form-filler',
  templateUrl: './form-filler.component.html',
  styleUrls: ['./form-filler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlaySpinnerComponent,
    NavBackComponent,
    LoadingContainerComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatchFormFillerComponent,
    GroupStageFormFillerComponent,
    TournamentGeneralFormFillerComponent,
    PlayoffFormFillerComponent,
    MatButton,
    MatIcon,
    DatePipe,
    ChampionsLeagueStageComponent,
  ],
})
export class FormFillerComponent extends StateComponent<FormFillerComponentState> implements OnInit, OnDestroy {
  afterSave = output();
  formType = FormType;
  
  readonly dataFetcher = input.required<() => Promise<IFormModel>>();
  readonly onSubmitCallback = input.required<(betting: IFormBettings<any>) => Promise<boolean>>();
  readonly backUrl = input('../../forms');
  readonly backElement = viewChild.required(CdkPortal);
  
  private readonly fb = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly notificationService = inject(NotificationService);

  state: FormFillerComponentState = {
    status: 'ready',
    tournamentId: this.route.snapshot.paramMap.get('tournamentId')!,
    form: undefined!,
    bettingForm: this.fb.group({}),
    isSubmissionMode: false,
  };

  @Input() set isSubmissionMode(isSubmissionMode: boolean) {
    this.updateState({ isSubmissionMode });
  }

  @Input() set disabled(disabled: boolean) {
    if (disabled) {
      this.invokeOnNextTick(() => {
        this.state.bettingForm.disable();
      });
    } else {
      this.state.bettingForm.enable();
    }
  }

  ngOnInit(): void {
    this.fetchData();
  }

  submit(): void {
    this.updateStatus('saving');
    this.onSubmitCallback()(this.state.bettingForm.value)
      .then((saved: boolean) => {
        if (saved) {
          this.afterSave.emit();
        }
      })
      .catch((e: Error) => {
        console.error(e);
        this.updateStatus('error');
        this.notificationService.notifyError();
      })
      .finally(() => {
        this.updateStatus('ready');
      });
  }

  private fetchData(): void {
    this.updateStatus('loading');
    this.dataFetcher()()
      .then((form) => {
        this.updateState({
          status: 'ready',
          form,
        });
      })
      .catch((e: Error) => {
        console.error(e);
        this.updateStatus('error');
      });
  }
}

interface FormFillerComponentState {
  status: ComponentStatus;
  tournamentId: string;
  form: IFormModel;
  bettingForm: UntypedFormGroup;
  isSubmissionMode: boolean;
}
